import { Modal } from '@kcftech/design-system';
import {
    AnalyticsModelCreateRequest,
    AnalyticsModelIndicatorChild,
    IAnalyticsModelResponse,
    MetricTypeCodes,
    PropertyRequestChild,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { Wizard, WizardStepConfig } from 'components/wizard/Wizard';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import {
    CreateModelStep1,
    CreateModelStep1FormData,
} from 'pages/Models/AnalyticsModelTab/CreateModelStep1';
import {
    CreateModelStep2,
    CreateModelStep2FormData,
} from 'pages/Models/AnalyticsModelTab/CreateModelStep2';
import {
    CreateModelStep3,
    CreateModelStep3FormData,
} from 'pages/Models/AnalyticsModelTab/CreateModelStep3';
import { CreateModelStep4 } from 'pages/Models/AnalyticsModelTab/CreateModelStep4';
import { useState } from 'react';
import { queryClient } from 'utils/query';
import { convertDuration } from 'utils/units';

export type CreateAnalyticsModelModalProps = {
    onClose: (result?: IAnalyticsModelResponse) => void;
    corporationId?: string;
    locationId?: string;
};

export function CreateAnalyticsModelModal({
    onClose,
    corporationId,
    locationId,
}: CreateAnalyticsModelModalProps) {
    const { showToast } = useNotificationContext();

    const [currentStep, setCurrentStep] = useState(0);

    const [step1Data, setStep1Data] = useState<CreateModelStep1FormData>(() => ({
        name: '',
        version: '',
        sourceCode: undefined,
        timeIntervalMinutes: undefined,
    }));

    const [step2Data, setStep2Data] = useState<CreateModelStep2FormData>(() => ({
        parameters: [],
    }));

    const [step3Data, setStep3Data] = useState<CreateModelStep3FormData>(() => ({
        featureIndicators: [],
        smartIndicators: [],
    }));

    const [confirmModalVisible, setConfirmModalVisible] = useState(false);

    const {
        mutate: saveModel,
        isPending: isSaving,
        isError,
    } = useMutation({
        meta: { name: 'save-model' },
        mutationFn: async () => {
            const data = { ...step1Data, ...step2Data, ...step3Data };
            const file = data.sourceCode?.getRawFile?.();

            if (!file) {
                throw new Error('Missing file');
            }

            if (!data.timeIntervalMinutes) {
                throw new Error('Missing timeIntervalMinutes');
            }

            const modelRequest = new AnalyticsModelCreateRequest({
                Name: data.name.trim(),
                Version: data.version.trim(),
                Indicators: [
                    ...data.featureIndicators.map(
                        ({ name, unit }) =>
                            new AnalyticsModelIndicatorChild({
                                IndicatorName: name.trim(),
                                IndicatorType: MetricTypeCodes.Feature,
                                IndicatorUnits: unit.trim(),
                            }),
                    ),
                    ...data.smartIndicators.map(
                        ({ name, unit }) =>
                            new AnalyticsModelIndicatorChild({
                                IndicatorName: name.trim(),
                                IndicatorType: MetricTypeCodes.SmartMetric,
                                IndicatorUnits: unit.trim(),
                            }),
                    ),
                ],
                Properties: data.parameters.map(
                    ({ label, type, isRequired }) =>
                        new PropertyRequestChild({
                            PropertyName: label.trim(),
                            PropertyValueType: type,
                            PropertyRequired: isRequired,
                        }),
                ),
                TriggerIntervalInSeconds: convertDuration(
                    data.timeIntervalMinutes,
                    'minutes',
                    'seconds',
                ),
                LocationId: locationId,
                CorporationId: corporationId,
            });

            // TODO: Update API to optionally allow supplying source code
            const newModel = await sdClient.createAnalyticsModels({ body: modelRequest });

            try {
                sdClient.updateAnalyticsModelSource({
                    modelId: newModel.Id,
                    file: {
                        data: file,
                        fileName: file.name,
                    },
                });
            } catch (e) {
                // If the model source fails to upload, delete the model
                await sdClient.deleteAnalyticsModel({ modelId: newModel.Id });
                throw e;
            }

            return newModel;
        },

        onSuccess: (result) => {
            queryClient.invalidateQueries({
                queryKey: ['analytic-models'],
            });
            setConfirmModalVisible(false);
            onClose(result);
            showToast({ type: 'success', content: 'Successfully created model' });
        },

        onError: () => showToast({ type: 'error', content: 'Failed to create model' }),
    });

    const steps: WizardStepConfig[] = [
        {
            key: 'model-details',
            title: 'Model Details',
            render: () => (
                <CreateModelStep1
                    corporationId={corporationId}
                    data={step1Data}
                    onCancel={onClose}
                    onNext={(data) => {
                        setStep1Data(data);
                        setCurrentStep((current) => current + 1);
                    }}
                />
            ),
        },
        {
            key: 'parameters',
            title: 'Parameters',
            render: () => (
                <CreateModelStep2
                    data={step2Data}
                    onCancel={onClose}
                    onBack={(data) => {
                        setStep2Data(data);
                        setCurrentStep((current) => current - 1);
                    }}
                    onNext={(data) => {
                        setStep2Data(data);
                        setCurrentStep((current) => current + 1);
                    }}
                />
            ),
        },
        {
            key: 'indicators',
            title: 'Indicators',
            render: () => (
                <CreateModelStep3
                    data={step3Data}
                    onCancel={onClose}
                    onBack={(data) => {
                        setStep3Data(data);
                        setCurrentStep((current) => current - 1);
                    }}
                    onNext={(data) => {
                        setStep3Data(data);
                        setCurrentStep((current) => current + 1);
                    }}
                />
            ),
        },
        {
            key: 'review',
            title: 'Review',
            render: () => (
                <CreateModelStep4
                    data={{ ...step1Data, ...step2Data, ...step3Data }}
                    onCancel={onClose}
                    onBack={() => setCurrentStep((current) => current - 1)}
                    onComplete={() => setConfirmModalVisible(true)}
                />
            ),
        },
    ];

    return (
        <>
            <Modal title="Create Model" height={750} width={1000} contentNoPadding>
                <Wizard
                    currentStepIndex={currentStep}
                    onStepChange={setCurrentStep}
                    steps={steps}
                />
            </Modal>
            {confirmModalVisible && (
                <ConfirmModal
                    width={600}
                    height={300}
                    title="Confirm Analytics Model Creation"
                    actionText="create the analytics model?"
                    actionButtonText="Create"
                    actionType="default"
                    closeButtonText="Cancel"
                    closable={!isSaving}
                    actionable={!isSaving}
                    onAction={saveModel}
                    onClose={() => setConfirmModalVisible(false)}
                    warningText={false}
                    checkText={false}
                    error={isError ? 'Failed to save model' : undefined}
                />
            )}
        </>
    );
}
